import type { IOffer } from "@bbj-types/offer";
import DomainIcon from "@mui/icons-material/Domain";
import { type IconButtonProps, type StackProps, Typography, type TypographyProps } from "@mui/material";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import { type IEnterpriseAutocomplete, TypoTooltip } from "@styleguide";
import clsx from "clsx";
import type { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { ApplyPopover } from "./ApplyPopover";
import { OfferPopover } from "./OfferPopover";
import { OfferCardEnterpriseStack, stackClasses } from "./styles";

interface IOfferCardEnterpriseProps {
  offer: Pick<IOffer, "source"> & Partial<Pick<IOffer, "enterprise" | "matchedEnterprise">>;
  prefix?: string;
  StackProps?: StackProps;
  TypographyProps?: TypographyProps<"div">;
  ButtonProps?: Omit<IconButtonProps, "onClick">;
  onEdit?: (enterprise: IEnterpriseAutocomplete | null) => Promise<void>;
  isLoadingPatchApply?: boolean;
}

export const OfferCardEnterprise = ({
  offer,
  onEdit,
  isLoadingPatchApply,
  prefix,
  StackProps,
  TypographyProps,
  ButtonProps,
}: IOfferCardEnterpriseProps) => {
  const enterprise = offer.enterprise;

  const matchedEnterprise =
    !isInternalOfferSource(offer.source) || offer.enterprise?._id === offer.matchedEnterprise?.enterpriseId
      ? offer.matchedEnterprise
      : undefined;

  return (
    <OfferCardEnterpriseStack {...StackProps} className={clsx(stackClasses.root, StackProps?.className)}>
      <DomainIcon color="secondary" className={stackClasses.icon} />
      {prefix && (
        <Typography variant="body1" className={stackClasses.prefix}>
          {prefix}
        </Typography>
      )}
      <TypoTooltip
        variant="body1"
        color="black.dark"
        noWrap
        {...(matchedEnterprise && {
          color: "primary.main",
          onClick: (evt: MouseEvent<HTMLSpanElement>) => evt.stopPropagation(),
          component: Link,
          to: `/v2/enterprise/${matchedEnterprise.enterpriseId}`,
          target: "_blank",
          rel: "noopener noreferrer",
        })}
        {...TypographyProps}
      >
        <span className={stackClasses.uppercase}>{matchedEnterprise?.name || enterprise?.name || "-"}</span>
        {matchedEnterprise &&
          matchedEnterprise.nbContacts > 0 &&
          ` (${matchedEnterprise.nbContacts} contact${matchedEnterprise.nbContacts > 1 ? "s" : ""})`}
      </TypoTooltip>
      {matchedEnterprise && enterprise?._id !== matchedEnterprise.enterpriseId && !onEdit && (
        <OfferPopover ButtonProps={ButtonProps} enterprise={enterprise} />
      )}
      {(!matchedEnterprise || enterprise?._id !== matchedEnterprise.enterpriseId) && onEdit && (
        <ApplyPopover
          ButtonProps={ButtonProps}
          enterprise={enterprise}
          onEdit={onEdit}
          matchedEnterprise={matchedEnterprise}
          loading={isLoadingPatchApply}
        />
      )}
    </OfferCardEnterpriseStack>
  );
};
